"use client";

import { FC, useEffect, useMemo, useState } from "react";
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Loader2 } from "lucide-react";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";

import MatchCard, { PickValue } from "../../components/MatchCard";
import PointsCard from "../../components/PointsCard";
import { useTranslation } from "../../hooks/useTranslation";
import { Week } from "@repo/types";

// Week 1 starts on the date in NEXT_PUBLIC_WEEK_ONE_START (Friday-Thursday weeks).
// Feature is hard-disabled when the env var is missing/invalid.
const WEEK_ONE_START_RAW = process.env.NEXT_PUBLIC_WEEK_ONE_START;
const WEEK_ONE_START = WEEK_ONE_START_RAW ? moment(WEEK_ONE_START_RAW) : null;
const WEEK_ONE_START_ENABLED = !!WEEK_ONE_START && WEEK_ONE_START.isValid();

interface UserPointsViewProps {
  weekId: number;
  isAdmin?: boolean;
}

type ApiResult = {
  round: number | null;
  winnerId: number | string | null;
  outcome: string | null;
};

type ApiPredictionItem = {
  PK: string;
  SK: string;
  userId: number;
  matchId: number;
  weekId: number;
  result: ApiResult;
  predictionTimestamp: string;
  matchDeadline: string;
  booster: boolean;
};

type AdminPredictionsResponse = {
  success: boolean;
  competitionFeed: string;
  seasonId: string;
  targetUserId: string;
  providerAccountId: string | null;
  userInfo: {
    name?: string;
    given_name?: string;
    family_name?: string;
    email?: string;
  } | null;
  favClub: { id: number; name: string } | null;
  predictions: ApiPredictionItem[];
  count: number;
};

type RankingEntryResponse = {
  userId: string;
  rank: number;
  score: number;
  userName?: string;
  correctPredictions?: number;
  totalPredictions?: number;
  accuracy?: number;
};

type UserRanksResponse = {
  generalRank: RankingEntryResponse | null;
  weekRank: RankingEntryResponse | null;
};

type ClubMatchesResponse = {
  matches: any[];
};

const pickFromApiPrediction = (prediction: ApiPredictionItem): PickValue => {
  const outcome = prediction.result?.outcome ?? null;
  const scoreMatch = outcome?.match(/^(\d+)-(\d+)$/);
  const homeGoals = scoreMatch ? Number(scoreMatch[1]) : null;
  const awayGoals = scoreMatch ? Number(scoreMatch[2]) : null;

  return {
    winnerId: prediction.result?.winnerId ?? null,
    outcome,
    round: prediction.result?.round ?? null,
    booster: !!prediction.booster,
    homeGoals,
    awayGoals,
  };
};

// ---------------------- API functions ----------------------
const fetchAdminPredictions = async (
  competitionFeed: string,
  seasonId: string | number,
  targetUserId: string
): Promise<AdminPredictionsResponse> => {
  const params = new URLSearchParams({
    competitionFeed,
    seasonId: String(seasonId),
    targetUserId,
  });

  const response = await fetch(`/api/admin/predictions?${params}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[fetchAdminPredictions] Error response:', errorText);
    throw new Error("Failed to fetch predictions");
  }

  return response.json();
};

const fetchUserRanks = async (
  competitionFeed: string,
  seasonId: string | number,
  userId: string,
  weekId?: number
): Promise<UserRanksResponse> => {
  const params = new URLSearchParams({
    competitionFeed,
    seasonId: String(seasonId),
    userId,
  });

  if (typeof weekId === "number" && weekId >= 0) {
    params.set("weekId", String(weekId));
  }

  const response = await fetch(`/api/rankings/user?${params}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch user ranks");
  }

  return response.json();
};

const fetchClubMatches = async (
  seasonId: number | string,
  clubId: number | string
): Promise<ClubMatchesResponse> => {
  const params = new URLSearchParams({
    seasonId: String(seasonId),
    clubId: String(clubId),
  });

  const response = await fetch(`/api/matches?${params.toString()}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch favorite club matches");
  }

  return response.json();
};

// ---------------------- Week Switch with navigation ----------------------
interface WeekSwitchProps {
  weekId: number;
  weeks: Week[];
  onWeekChange: (newWeekId: number) => void;
}

const WeekSwitchNavigation: React.FC<WeekSwitchProps> = ({ weekId, weeks, onWeekChange }) => {
  const { t } = useTranslation();

  const currentWeekIndex = weeks.findIndex((week: Week) => week.weekId === weekId);
  const canGoPrevious = currentWeekIndex > 0;
  const canGoNext = currentWeekIndex < weeks.length - 1;

  const goToPreviousWeek = () => {
    if (canGoPrevious) {
      const newWeekId = weeks[currentWeekIndex - 1]!.weekId;
      onWeekChange(newWeekId);
    }
  };

  const goToNextWeek = () => {
    if (canGoNext) {
      const newWeekId = weeks[currentWeekIndex + 1]!.weekId;
      onWeekChange(newWeekId);
    }
  };

  return (
    <div className="bg-[var(--bet-red)] text-white rounded-2xl h-16 w-full flex gap-[41px] items-center justify-center p-4 lg:grid lg:grid-cols-[1fr_auto_1fr]">
      <button 
        onClick={goToPreviousWeek}
        disabled={!canGoPrevious}
        className="relative shrink-0 size-6 cursor-pointer hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed lg:justify-self-start"
      >
        <div className="absolute flex h-6 items-center justify-center left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] w-[14px]">
          <div className="flex-none rotate-[180deg] scale-y-[-100%]">
            <div className="h-6 relative w-[14px]">
              <img alt="" className="block max-w-none size-full" src="/assets/e5231f95e42e099ebaaec0209df96f88062208c7.svg" />
            </div>
          </div>
        </div>
      </button>
      <div className="basis-0 flex flex-col font-bold grow justify-center leading-[0] min-h-px min-w-px relative shrink-0 text-[18px] text-white text-center lg:col-start-2">
        <p className="leading-[18px]">{t('predictions.week')} {weekId}</p>
      </div>
      <button 
        onClick={goToNextWeek}
        disabled={!canGoNext}
        className="relative shrink-0 size-6 cursor-pointer hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed lg:justify-self-end lg:col-start-3"
      >
        <div className="absolute h-6 left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] w-[14px]">
          <img alt="" className="block max-w-none size-full" src="/assets/e5231f95e42e099ebaaec0209df96f88062208c7.svg" />
        </div>
      </button>
    </div>
  );
};

// ---------------------- User Points View ----------------------
const UserPointsView: FC<UserPointsViewProps> = ({ weekId: initialWeekId, isAdmin }) => {
  const { t } = useTranslation();
  const { competitionConfig, competitionData } = useCompetitionContext();
  const { targetUserId } = useParams<{ targetUserId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Local state for selected week - allows navigation through weeks
  const [selectedWeekId, setSelectedWeekId] = useState<number>(initialWeekId);

  // Get weeks from competition data
  const weeks = competitionData.weeks || [];

  // Initialize selectedWeekId when competitionData loads
  useEffect(() => {
    if (weeks.length > 0 && selectedWeekId === 0) {
      // Default to the deadlineWeek - 1 (most recent completed week) or first available
      const deadlineWeek = competitionData.deadlineInfo?.deadlineWeek;
      const defaultWeek = deadlineWeek ? Math.max(deadlineWeek - 1, 1) : weeks[0]?.weekId ?? 1;
      setSelectedWeekId(defaultWeek);
    }
  }, [weeks, selectedWeekId, competitionData.deadlineInfo]);

  // Redirect non-admins
  useEffect(() => {
    if (!isAdmin) {
      console.warn('[UserPointsView] Non-admin access attempt, redirecting...');
      navigate('/fantasy/rankings', { replace: true });
    }
  }, [isAdmin, navigate]);

  // Fetch predictions for the target user (all predictions, not just current week)
  const {
    data: predictionsData,
    isLoading: isLoadingPredictions,
    error: predictionsError,
  } = useQuery({
    queryKey: ["admin-predictions", competitionConfig.competitionFeed, targetUserId],
    queryFn: () =>
      fetchAdminPredictions(
        competitionConfig.competitionFeed,
        competitionConfig.seasonId,
        targetUserId!
      ),
    enabled: !!isAdmin && !!competitionConfig.competitionFeed && !!targetUserId,
  });

  // Fetch ranking data for the target user (updates when week changes)
  const {
    data: userRanksData,
    isLoading: isLoadingUserRanks,
  } = useQuery<UserRanksResponse>({
    queryKey: [
      "user-ranks",
      competitionConfig.competitionFeed,
      competitionConfig.seasonId,
      targetUserId,
      selectedWeekId,
    ],
    queryFn: () =>
      fetchUserRanks(
        competitionConfig.competitionFeed,
        competitionConfig.seasonId,
        targetUserId!,
        selectedWeekId
      ),
    enabled: !!isAdmin && !!competitionConfig?.competitionFeed && !!targetUserId && selectedWeekId > 0,
    staleTime: 30_000,
  });

  // Get favorite club ID from predictions data
  const favoriteClubId = predictionsData?.favClub?.id
    ? Number(predictionsData.favClub.id)
    : null;

  // Fetch favorite club matches
  const { data: favoriteClubMatchesData } = useQuery<ClubMatchesResponse>({
    queryKey: [
      "favorite-club-matches",
      competitionConfig?.seasonId,
      favoriteClubId,
    ],
    queryFn: () =>
      fetchClubMatches(competitionConfig.seasonId, favoriteClubId!),
    enabled: Boolean(
      favoriteClubId &&
        competitionConfig?.seasonId &&
        competitionConfig?.competitionFeed
    ),
    staleTime: 60_000,
  });

  // Matches for selected week
  const weekMatches = useMemo(() => {
    return (competitionData.matches || [])
      .filter((m: any) => m.weekId === selectedWeekId)
      .filter((m: any) => m?.home?.id && m?.away?.id)
      .map((match: any) => {
        const matchStats = competitionData.stats?.find?.((s: any) => s?.m === match.id);
        return {
          ...match,
          stats: matchStats ?? null,
        };
      });
  }, [competitionData.matches, competitionData.stats, selectedWeekId]);

  // Calculate favorite club extra matches for the week (not already in weekMatches)
  const favoriteClubExtras = useMemo(() => {
    if (!WEEK_ONE_START_ENABLED || !WEEK_ONE_START) {
      return [];
    }
    if (!favoriteClubMatchesData?.matches?.length || !favoriteClubId) {
      return [];
    }

    // Week 1 starts on the date in NEXT_PUBLIC_WEEK_ONE_START.
    // Each subsequent week starts 7 days later.
    const weekStart = WEEK_ONE_START.clone().add((selectedWeekId - 1) * 7, "days");
    const weekEnd = weekStart.clone().add(6, "days").endOf("day");
    
    const seenMatchIds = new Set(weekMatches.map((match: any) => match.id));

    const matchesInWindow = favoriteClubMatchesData.matches
      .filter((match: any) => {
        if (!match?.date) return false;
        const matchMoment = moment(match.date);
        return matchMoment.isBetween(weekStart, weekEnd, undefined, "[]");
      })
      .filter((match: any) => !seenMatchIds.has(match.id))
      .sort((a: any, b: any) => {
        const aTime = new Date(a.date || 0).getTime();
        const bTime = new Date(b.date || 0).getTime();
        return aTime - bTime;
      });

    return matchesInWindow.slice(0, 1);
  }, [favoriteClubMatchesData?.matches, favoriteClubId, weekMatches, selectedWeekId]);

  // Combine league matches with favorite club extras
  const allWeekMatches = useMemo(
    () => [...weekMatches, ...favoriteClubExtras],
    [weekMatches, favoriteClubExtras]
  );

  // Derive picks from predictions for selected week
  const picks = useMemo(() => {
    if (!predictionsData?.predictions) return {};

    const result: Record<number, PickValue> = {};
    predictionsData.predictions
      .filter((p) => p.weekId === selectedWeekId)
      .forEach((p: ApiPredictionItem) => {
        result[p.matchId] = pickFromApiPrediction(p);
      });

    return result;
  }, [predictionsData, selectedWeekId]);

  // Calculate total predictions across all weeks
  const totalPredictionsAllWeeks = predictionsData?.predictions?.length ?? 0;

  // Split matches into upcoming and past
  const { upcomingMatches, pastMatches } = useMemo(() => {
    const now = moment();
    const upcoming: any[] = [];
    const past: any[] = [];

    allWeekMatches.forEach((match: any) => {
      if (!match?.date) {
        upcoming.push(match);
        return;
      }

      const matchMoment = moment(match.date);
      if (!matchMoment.isValid()) {
        upcoming.push(match);
        return;
      }

      if (matchMoment.isAfter(now)) {
        upcoming.push(match);
      } else {
        past.push(match);
      }
    });

    // Sort upcoming: earliest first (ascending)
    upcoming.sort((a: any, b: any) => {
      const aTime = a?.date ? moment(a.date).valueOf() : 0;
      const bTime = b?.date ? moment(b.date).valueOf() : 0;
      return aTime - bTime;
    });

    // Sort past: latest first (descending)
    past.sort((a: any, b: any) => {
      const aTime = a?.date ? moment(a.date).valueOf() : 0;
      const bTime = b?.date ? moment(b.date).valueOf() : 0;
      return bTime - aTime;
    });

    return { upcomingMatches: upcoming, pastMatches: past };
  }, [allWeekMatches]);

  const matchesToRender = useMemo(
    () => [...upcomingMatches, ...pastMatches],
    [upcomingMatches, pastMatches]
  );

  // Helper to parse odds
  const parseOdds = (rawOdds: any) => {
    if (!rawOdds) return { home: 0, draw: 0, away: 0 };
    try {
      const parsed = typeof rawOdds === "string" ? JSON.parse(rawOdds) : rawOdds;
      return {
        home: parsed["1"] ?? parsed.home ?? 0,
        draw: parsed["X"] ?? parsed.draw ?? 0,
        away: parsed["2"] ?? parsed.away ?? 0,
      };
    } catch {
      return { home: 0, draw: 0, away: 0 };
    }
  };

  // Go back handler
  const handleGoBack = () => {
    // Check if we came from rankings page
    if (location.state?.from === 'rankings') {
      navigate(-1);
    } else {
      navigate('/fantasy/rankings');
    }
  };

  // Show access denied for non-admins
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[var(--bet-background)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 p-8">
          <p className="text-gray-600">{t('admin.accessDenied') || 'Access denied'}</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingPredictions) {
    return (
      <div className="min-h-screen bg-[var(--bet-background)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--bet-red)]" />
          <p className="text-gray-600">{t('predictions.loading')}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (predictionsError) {
    return (
      <div className="min-h-screen bg-[var(--bet-background)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 p-8">
          <p className="text-red-600">Error loading predictions</p>
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-[var(--bet-red)] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to rankings
          </button>
        </div>
      </div>
    );
  }

  // Get user name from ranks data
  const targetUserName = userRanksData?.generalRank?.userName || 
                         userRanksData?.weekRank?.userName || 
                         `User ${targetUserId}`;

  return (
    <div className="my-6 md:m-6 lg:m-10 pb-20">
      {/* Back button and header */}
      <div className="mb-4">
        <button
          onClick={handleGoBack}
          className="flex items-center gap-2 text-[var(--bet-red)] hover:underline mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.backToRankings') || 'Back to rankings'}
        </button>
        
        <div className="bg-white rounded-2xl p-4 mb-4" style={{ boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)" }}>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-[var(--bet-red)] text-white px-2 py-1 rounded font-medium">
              ADMIN VIEW
            </span>
            <h1 className="text-xl font-bold text-[var(--bet-text)]">
              {t('admin.viewingPredictions') || 'Viewing predictions for'}: {targetUserName}
            </h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            User ID: {targetUserId}
            {predictionsData?.providerAccountId && (
              <span className="ml-2">| RTBF ID: {predictionsData.providerAccountId}</span>
            )}
          </p>
          {predictionsData?.userInfo && (
            <p className="text-sm text-gray-500">
              {predictionsData.userInfo.name} ({predictionsData.userInfo.email})
            </p>
          )}
          {predictionsData?.favClub && (
            <p className="text-sm text-gray-500">
              Favorite Club: {predictionsData.favClub.name} (ID: {predictionsData.favClub.id})
            </p>
          )}
        </div>
      </div>

      {/* Week navigation */}
      <div className="relative overflow-hidden rounded-2xl mx-auto max-w-6xl bg-black">
        <WeekSwitchNavigation 
          weekId={selectedWeekId} 
          weeks={weeks} 
          onWeekChange={setSelectedWeekId} 
        />
      </div>

      {/* Points Card and matches grid */}
      <div className="mt-2 md:mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        {/* Points Card */}
        <PointsCard
          className="mt-6"
          isLoading={isLoadingUserRanks}
          weekRank={
            userRanksData?.weekRank
              ? {
                  rank: userRanksData.weekRank.rank,
                  score: userRanksData.weekRank.score,
                }
              : null
          }
          overallRank={
            userRanksData?.generalRank
              ? {
                  rank: userRanksData.generalRank.rank,
                  score: userRanksData.generalRank.score,
                }
              : null
          }
        />

        {/* Predictions count summary */}
        <div
          className="bg-white rounded-[16px] p-6 md:p-8 mt-6"
          style={{ boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)" }}
        >
          <div className="text-center space-y-3">
            <div>
              <p className="text-sm text-gray-600">
                {t('admin.weekPredictions') || `Week ${selectedWeekId} predictions`}
              </p>
              <p className="text-3xl font-bold text-[var(--bet-text)]">
                {Object.keys(picks).length} / {allWeekMatches.length}
              </p>
            </div>
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                {t('admin.totalAllWeeks') || 'Total predictions (all weeks)'}
              </p>
              <p className="text-lg font-semibold text-gray-700">
                {totalPredictionsAllWeeks}
              </p>
            </div>
          </div>
        </div>

        {/* Upcoming matches */}
        {upcomingMatches.map((match: any) => {
          const hasSides = !!(match?.home?.id && match?.away?.id);
          const current: PickValue =
            picks[match.id] ?? {
              winnerId: null,
              outcome: null,
              round: null,
              booster: false,
            };

          if (!hasSides) {
            return (
              <Card key={match.id} className="bg-white rounded-2xl border border-gray-200">
                <CardContent className="p-5 md:p-6">
                  <div className="text-sm text-amber-700">
                    {t('predictions.matchIncomplete')}
                  </div>
                </CardContent>
              </Card>
            );
          }

          return (
            <MatchCard
              key={match.id}
              match={match}
              pick={current}
              seasonId={competitionConfig.seasonId}
              onPickChange={() => {}} // No-op for read-only
              disabled={true} // Always disabled
              isSaving={false}
              odds={parseOdds(match.odds)}
              isFavorite={match?.home?.id === favoriteClubId || match?.away?.id === favoriteClubId}
              boosterDisabled={true}
            />
          );
        })}

        {/* Past matches section */}
        {pastMatches.length > 0 && (
          <div className="col-span-full mt-4">
            <h2 className="text-lg font-bold text-[var(--bet-text)]">
              {t('predictions.pastMatches')}
            </h2>
          </div>
        )}

        {pastMatches.map((match: any) => {
          const hasSides = !!(match?.home?.id && match?.away?.id);
          const current: PickValue =
            picks[match.id] ?? {
              winnerId: null,
              outcome: null,
              round: null,
              booster: false,
            };

          if (!hasSides) {
            return (
              <Card key={match.id} className="bg-white rounded-2xl border border-gray-200">
                <CardContent className="p-5 md:p-6">
                  <div className="text-sm text-amber-700">
                    {t('predictions.matchIncomplete')}
                  </div>
                </CardContent>
              </Card>
            );
          }

          return (
            <MatchCard
              key={match.id}
              match={match}
              pick={current}
              seasonId={competitionConfig.seasonId}
              onPickChange={() => {}} // No-op for read-only
              disabled={true} // Always disabled
              isSaving={false}
              odds={parseOdds(match.odds)}
              isFavorite={match?.home?.id === favoriteClubId || match?.away?.id === favoriteClubId}
              boosterDisabled={true}
            />
          );
        })}

        {matchesToRender.length === 0 && (
          <div className="col-span-full rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
            <div className="text-lg font-semibold">
              {t('predictions.noMatches')}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {t('predictions.checkBack')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPointsView;
